import { MimeType } from 'vet-bones/constants';

import { DataSourceTypeDescription } from 'src/constants/dataSourceTypeDescription';
import {
  Mapping,
  RelationshipMapping,
} from 'src/containers/ProjectDiagram/types/data';
import { DiagramSnapshot } from 'src/containers/ProjectDiagram/types/diagram';
import { dataSourceNativeQuoteCharacter } from 'src/selectors/dataSourceNativeQuoteCharacter';
import {
  ProjectVirtualGraph,
  ProjectVirtualGraphOptions,
  defaultProjectVirtualGraphOptions,
} from 'src/store/state/projects';
import { newline } from 'src/templates/copy';
import { ExportVirtualGraphOptions } from 'src/utils/export/types';
import { generateSqlQueryForMapping } from 'src/utils/generateSqlQueryForMapping';
import { stringifyVirtualGraphMappings } from 'src/utils/stringify/mappings/virtualGraphMappings';
import { stringifyProperties } from 'src/utils/stringify/properties';
import { stringToFilename } from 'src/utils/stringToFilename';

const exportMappings = ({
  id,
  diagram,
  dataNamespace,
  typeDescription,
  virtualGraphsAndMappings,
}: {
  id: string;
  diagram: DiagramSnapshot;
  dataNamespace: string;
  typeDescription: DataSourceTypeDescription;
  virtualGraphsAndMappings: Array<{
    classMappings: Mapping[];
    relationshipMappings: RelationshipMapping[];
    virtualGraph: ProjectVirtualGraph;
  }>;
}) => {
  const filename = `${stringToFilename(id)}.sms`;
  const stringifiedMappings = virtualGraphsAndMappings
    .map(({ classMappings, relationshipMappings, virtualGraph }) => {
      const nativeQuoteCharacter = dataSourceNativeQuoteCharacter(
        typeDescription,
        virtualGraph.dataSourceType
      );

      let aliasesByColumnName: Record<string, string> | null = null;
      let query: string;
      if (typeof virtualGraph.from === 'string') {
        query = virtualGraph.from;
      } else {
        const { aliasesByColumnName: aliases, query: sqlQuery } =
          generateSqlQueryForMapping({
            fields: virtualGraph.fields,
            nativeQuoteCharacter,
            options: virtualGraph.options,
            table: virtualGraph.from,
          });
        aliasesByColumnName = aliases;
        query = sqlQuery;
      }
      const mappingString = stringifyVirtualGraphMappings({
        aliasesByColumnName,
        classMappings,
        model: diagram.model,
        relationshipMappings,
        dataNamespace,
        query,
      });
      return `# ${virtualGraph.name}\n${mappingString}`;
    })
    .join('\n\n;\n\n');
  const contents = stringifiedMappings + newline;
  return { contents, filename, mimeType: MimeType.SMS };
};

const exportProperties = (id: string, options: ProjectVirtualGraphOptions) => {
  const filename = `${stringToFilename(id)}.properties`;
  const contents = stringifyProperties(options) + newline;
  return { contents, filename, mimeType: MimeType.TEXT };
};

export const exportVirtualGraph = ({
  id,
  diagram,
  dataNamespace,
  typeDescription,
  virtualGraphsAndMappings,
}: {
  id: string;
  diagram: DiagramSnapshot;
  dataNamespace: string;
  typeDescription: DataSourceTypeDescription;
  virtualGraphsAndMappings: Array<{
    classMappings: Mapping[];
    relationshipMappings: RelationshipMapping[];
    virtualGraph: ProjectVirtualGraph;
  }>;
}): ExportVirtualGraphOptions => {
  const mappingsOptions = exportMappings({
    id,
    diagram,
    dataNamespace,
    typeDescription,
    virtualGraphsAndMappings,
  });

  const propertiesOptions = exportProperties(
    id,
    virtualGraphsAndMappings[0].virtualGraph.options ||
      defaultProjectVirtualGraphOptions
  );

  return { mappingsOptions, propertiesOptions };
};
